// Delivery Assignment Board — Kanban + list view for managing deliveries
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LayoutGrid, List, RefreshCw, AlertCircle } from "lucide-react";
import { Button, Skeleton } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { DeliveryCard } from "@/components/admin/delivery/DeliveryCard";
import { DriverAssignModal } from "@/components/admin/delivery/DriverAssignModal";
import { useDeliveryBoard, KANBAN_COLUMNS } from "@/hooks/admin/useDeliveryBoard";
import type { Delivery, DeliveryStatus, AssignDriverPayload } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLUMN_STYLES: Record<DeliveryStatus, { bg: string; border: string; badge: string }> = {
  unassigned: {
    bg: "bg-stone-50",
    border: "border-stone-200",
    badge: "bg-stone-100 text-stone-600",
  },
  assigned: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-700",
  },
  in_transit: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
  },
  delivered: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
  },
};

const COLUMN_LABELS: Record<DeliveryStatus, string> = {
  unassigned: "Non assigné",
  assigned: "Assigné",
  in_transit: "En transit",
  delivered: "Livré",
};

// ---------------------------------------------------------------------------
// List columns definition
// ---------------------------------------------------------------------------

const LIST_COLUMNS: readonly ColumnDef<Delivery>[] = [
  {
    id: "public_ref",
    header: "Référence",
    cell: (d) => (
      <span className="font-medium text-stone-900">{d.order.public_ref}</span>
    ),
  },
  {
    id: "customer",
    header: "Client",
    cell: (d) => (
      <div>
        <p className="text-sm text-stone-900">{d.order.customer_name}</p>
        <p className="text-xs text-stone-500">{d.order.customer_phone}</p>
      </div>
    ),
  },
  {
    id: "address",
    header: "Adresse",
    cell: (d) => (
      <span className="line-clamp-1 text-sm text-stone-600">
        {d.address ?? d.order.delivery_address}
      </span>
    ),
  },
  {
    id: "zone",
    header: "Zone",
    cell: (d) =>
      d.zone ? (
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
          {d.zone.name}
        </span>
      ) : (
        <span className="text-stone-400">—</span>
      ),
  },
  {
    id: "driver",
    header: "Chauffeur",
    cell: (d) =>
      d.driver ? (
        <span className="text-sm text-stone-900">{d.driver.full_name}</span>
      ) : (
        <span className="text-xs italic text-stone-400">Non assigné</span>
      ),
  },
  {
    id: "status",
    header: "Statut",
    cell: (d) => {
      const style = COLUMN_STYLES[d.status as DeliveryStatus];
      return (
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${style?.badge ?? "bg-stone-100 text-stone-600"}`}
        >
          {COLUMN_LABELS[d.status as DeliveryStatus] ?? d.status}
        </span>
      );
    },
  },
];

// ---------------------------------------------------------------------------
// Kanban column component
// ---------------------------------------------------------------------------

interface KanbanColumnProps {
  readonly status: DeliveryStatus;
  readonly deliveries: readonly Delivery[];
  readonly onCardClick: (delivery: Delivery) => void;
}

function KanbanColumn({ status, deliveries, onCardClick }: KanbanColumnProps) {
  const style = COLUMN_STYLES[status];
  const label = COLUMN_LABELS[status];
  const canAssign = status === "unassigned" || status === "assigned";

  return (
    <div
      className={`flex min-h-48 flex-col rounded-lg border ${style.border} ${style.bg}`}
    >
      {/* Column header */}
      <div className="flex items-center justify-between border-b border-stone-200/60 px-3 py-2.5">
        <span className="text-sm font-semibold text-stone-700">{label}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${style.badge}`}
        >
          {deliveries.length}
        </span>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-2 overflow-y-auto p-2">
        {deliveries.length === 0 ? (
          <p className="py-6 text-center text-xs text-stone-400">
            Aucune livraison
          </p>
        ) : (
          deliveries.map((delivery) => (
            <DeliveryCard
              key={delivery.id}
              delivery={delivery}
              onAssign={canAssign ? onCardClick : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function DeliveryBoard() {
  useAdminPageTitle("Livraisons");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t: _t } = useTranslation("logistics");

  const {
    columns,
    drivers,
    zones,
    isLoading,
    isError,
    refetch,
    filters,
    updateFilter,
    viewMode,
    setViewMode,
    assignDriver,
    isAssigning,
    totalCount,
  } = useDeliveryBoard();

  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);

  const allDeliveries: readonly Delivery[] = [
    ...columns.unassigned,
    ...columns.assigned,
    ...columns.in_transit,
    ...columns.delivered,
  ];

  const handleCardClick = useCallback((delivery: Delivery) => {
    setSelectedDelivery(delivery);
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedDelivery(null);
  }, []);

  const handleAssign = useCallback(
    async (deliveryId: string, payload: AssignDriverPayload) => {
      await assignDriver({ deliveryId, payload });
    },
    [assignDriver],
  );

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Page header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              Tableau des livraisons
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              {isLoading
                ? "Chargement…"
                : `${totalCount} livraison(s) au total`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex rounded-md border border-stone-200 bg-white">
              <button
                type="button"
                onClick={() => setViewMode("kanban")}
                className={`flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-sm transition-colors ${
                  viewMode === "kanban"
                    ? "bg-amber-100 text-amber-800"
                    : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                <LayoutGrid className="h-4 w-4" />
                Kanban
              </button>
              <button
                type="button"
                onClick={() => setViewMode("map")}
                className={`flex items-center gap-1.5 rounded-r-md border-l border-stone-200 px-3 py-1.5 text-sm transition-colors ${
                  viewMode === "map"
                    ? "bg-amber-100 text-amber-800"
                    : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                <List className="h-4 w-4" />
                Liste
              </button>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filters.zone_id ?? ""}
            onChange={(e) => updateFilter("zone_id", e.target.value || null)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Toutes les zones</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filters.date_from ?? ""}
            onChange={(e) => updateFilter("date_from", e.target.value || null)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
          <span className="text-sm text-stone-400">–</span>
          <input
            type="date"
            value={filters.date_to ?? ""}
            onChange={(e) => updateFilter("date_to", e.target.value || null)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les livraisons.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {KANBAN_COLUMNS.map((col) => (
              <div
                key={col.id}
                className="rounded-lg border border-stone-200 bg-stone-50 p-3"
              >
                <Skeleton className="mb-3 h-5 w-24" />
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="mb-2 h-28 w-full rounded-lg" />
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Kanban board */}
        {!isLoading && !isError && viewMode === "kanban" && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {KANBAN_COLUMNS.map((col) => (
              <KanbanColumn
                key={col.id}
                status={col.id}
                deliveries={columns[col.id]}
                onCardClick={handleCardClick}
              />
            ))}
          </div>
        )}

        {/* List view */}
        {!isLoading && !isError && viewMode === "map" && (
          <>
            {allDeliveries.length === 0 ? (
              <EmptyState
                title="Aucune livraison"
                description="Aucune livraison ne correspond aux filtres sélectionnés."
              />
            ) : (
              <AdminDataTable
                data={allDeliveries}
                columns={LIST_COLUMNS}
                isLoading={false}
              />
            )}
          </>
        )}
      </div>

      {/* Assign driver modal */}
      <DriverAssignModal
        open={selectedDelivery !== null}
        onClose={handleModalClose}
        delivery={selectedDelivery}
        drivers={[...drivers]}
        onAssign={handleAssign}
        isAssigning={isAssigning}
      />
    </AdminLayout>
  );
}
